import { useMemo } from "react";
import { Operation } from "rfc6902";
import {
    CreateTissueOfferRequest,
    DefaultPagedResponse,
    Entity,
    IUseQueryPaged,
    MutationResult,
    PagedResponse,
    QueryHookParams,
    QueryType,
    TissueOffer,
} from "../models";
import { useAxios, useGenericMutation, useGenericQuery } from "../hooks";
import { useAuth, useQueryParams } from "../contexts";
import { Endpoints, queryKeys } from "../api";
import { getQueryType, getRequestOptions } from "../helper-functions";

export interface IUseTissueOfferQuery extends Omit<IUseQueryPaged<TissueOffer>, "upsertMutation" | "createMutation"> {
    createMutation: MutationResult<TissueOffer, unknown, CreateTissueOfferRequest>;
}

export const useTissueOfferQuery = (params?: QueryHookParams): IUseTissueOfferQuery => {
    const offerId = isNaN(Number(params?.id)) ? 0 : Number(params?.id);
    const { isAuthorized } = useAuth();
    const { get, post, patch, del } = useAxios();
    const { queryParams, sortParams, pageParams } = useQueryParams().getParamsApi(Entity.TissueOffers);
    const url = Endpoints.TissueOffers;
    const queryType = getQueryType(offerId, params?.queryType);

    const pagedListQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<TissueOffer>>(getRequestOptions(url, config, queryParams, sortParams, pageParams)),
        queryKey: queryKeys.tissueOffers.pagedList({ ...queryParams, ...pageParams, ...sortParams }),
        placeholderData: DefaultPagedResponse,
        enabled: isAuthorized && queryType === QueryType.PagedList,
    });

    const listQuery = useGenericQuery({
        queryFn: (config) =>
            get<PagedResponse<TissueOffer>>(
                getRequestOptions(url, config, undefined, sortParams, undefined, false, true)
            ).then((response) => response.items),
        queryKey: queryKeys.tissueOffers.list({ ...queryParams, ...sortParams }),
        placeholderData: [],
        enabled: isAuthorized && queryType === QueryType.List,
    });

    const detailQuery = useGenericQuery({
        queryFn: (config) => get<TissueOffer>({ url: `${url}/${offerId}`, config }),
        queryKey: queryKeys.tissueOffers.detail(offerId),
        placeholderData: undefined,
        enabled: isAuthorized && queryType === QueryType.Detail && !!params?.id,
    });

    const createMutation = useGenericMutation({
        mutationFn: (data: CreateTissueOfferRequest) => post<CreateTissueOfferRequest, TissueOffer>({ url, data }),
        queryKey: queryKeys.tissueOffers.all(),
        keysToInvalidate: [
            queryKeys.tissueOffers.lists(),
            queryKeys.tissueOffers.pagedLists(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
            queryKeys.surgeries.all(),
        ],
        successToast: "Tissue offer created",
    });

    const updateMutation = useGenericMutation({
        mutationFn: (data: Operation[]) => patch<Operation[], TissueOffer>({ url: `${url}/${offerId}`, data }),
        queryKey: queryKeys.tissueOffers.detail(offerId),
        keysToInvalidate: [
            queryKeys.tissueOffers.lists(),
            queryKeys.tissueOffers.pagedLists(),
            queryKeys.lists.all(),
            queryKeys.dashboards.distribution(),
            queryKeys.dashboards.surgery(),
            queryKeys.surgeries.all(),
        ],
        successToast: "Tissue offer updated",
    });

    const deleteMutation = useGenericMutation({
        mutationFn: () => del({ url: `${url}/${offerId}` }),
        queryKey: queryKeys.tissueOffers.detail(offerId),
        keysToInvalidate: [queryKeys.tissueOffers.lists(), queryKeys.tissueOffers.pagedLists()],
        successToast: "Tissue offer deleted",
    });

    return useMemo(
        () => ({
            pagedListQuery,
            listQuery,
            detailQuery,
            createMutation,
            updateMutation,
            deleteMutation,
        }),
        [pagedListQuery, listQuery, detailQuery, createMutation, updateMutation, deleteMutation]
    );
};
